import {
  ApiError,
  AUTH_REQUIRED_EVENT,
  type OpsAssistantHistoryMessage,
  type OpsAssistantWorkspaceContext
} from "../api.js";
import {
  decodeOpsPublicTurnEnvelope,
  isUnicodeScalarTextWithinLength,
  opsSha256Utf8,
  type OpsPublicTurnEnvelope,
  type OpsSha256
} from "@business-ops/domain";

export interface OpsLiveTurnProgress {
  phase: "connected" | "checking" | "heartbeat";
  requiresCheck: boolean;
  elapsedMs: number;
  bytesReceived: number;
}

export type OpsLiveTurnTerminalOutcome =
  | "COMPLETED_FINAL"
  | "SERVER_CLARIFICATION"
  | "SERVER_FAILURE"
  | "ABORTED"
  | "PREMATURE_EOF"
  | "RELEASE_ENVELOPE_REJECTED"
  | "TRANSPORT_READ_FAILED"
  | "NONCANONICAL_TRANSPORT_ERROR"
  | "HTTP_ERROR"
  | "INVALID_FRAME"
  | "RESPONSE_TOO_LARGE";

export interface OpsLiveTurnTerminalReceipt {
  schema: "ops.client-stream-terminal.v1";
  requestKeyHash: string | null;
  outcome: OpsLiveTurnTerminalOutcome;
  safeErrorCode: string | null;
  lastFrameKind: "heartbeat" | "progress" | "final" | "error" | null;
  bytesReceived: number;
  elapsedMs: number;
  httpStatus: number | null;
  contentType: "application/x-ndjson" | "application/json" | "other" | null;
}

export interface OpsLiveTurnOptions {
  expectedReleaseId: OpsSha256;
  signal?: AbortSignal;
  onProgress?: (progress: OpsLiveTurnProgress) => void;
  /** Ephemeral presentation only; never enters request history or the signed final. */
  onModelProgress?: (text: string) => void;
  fetcher?: typeof fetch;
  now?: () => number;
  onTerminal?: (receipt: OpsLiveTurnTerminalReceipt) => void;
  requestKey?: string;
  sourceEventIds?: readonly string[];
  /** Retained playback-only attachments; never evidence input. */
  presentationSourceEventIds?: readonly string[];
  workspaceContext?: OpsAssistantWorkspaceContext | null;
}

const MAX_RESPONSE_BYTES = 1_000_000;
const OPS_LUNA_STREAM_MEDIA_TYPE = "application/x-ndjson";
const OPS_PUBLIC_TURN_PERSISTENCE_UNRESOLVED = "OPS_PUBLIC_TURN_PERSISTENCE_UNRESOLVED" as const;
const OPS_PUBLIC_TURN_PERSISTENCE_UNRESOLVED_MESSAGE =
  "The app couldn't confirm whether Luna's result was saved. Try the same request again." as const;
const SAFE_HTTP_ERROR_CODES = new Map<string, string>([
  ["INTERNAL_ERROR", "INTERNAL_ERROR"],
  ["INVALID_INPUT", "INVALID_INPUT"],
  ["INVALID_ORIGIN", "INVALID_ORIGIN"],
  ["PUBLIC_TURN_STALE", "PUBLIC_TURN_STALE"],
  ["RATE_LIMITED", "RATE_LIMITED"],
  ["REQUEST_FAILED", "REQUEST_FAILED"],
  ["UNAUTHORIZED", "UNAUTHORIZED"]
]);

export async function requestLiveLunaOps(
  text: string,
  history: readonly OpsAssistantHistoryMessage[],
  options: OpsLiveTurnOptions
): Promise<OpsPublicTurnEnvelope> {
  const fetcher = options.fetcher ?? fetch;
  const now = options.now ?? (() => performance.now());
  const startedAt = now();
  let bytesReceived = 0;
  let lastFrameKind: OpsLiveTurnTerminalReceipt["lastFrameKind"] = null;
  let terminalRecorded = false;
  let httpStatus: number | null = null;
  let contentType: OpsLiveTurnTerminalReceipt["contentType"] = null;
  const requestKeyHash = options.requestKey
    ? opsSha256Utf8(options.requestKey).slice("sha256:".length)
    : null;
  const requestWasAborted = (): boolean => options.signal?.aborted === true;
  const recordTerminal = (outcome: OpsLiveTurnTerminalOutcome, safeErrorCode: string | null): void => {
    if (terminalRecorded) return;
    terminalRecorded = true;
    // WHY [BOL-LUNA-CLIENT-TERMINAL-RECEIPT-001]: a 200 NDJSON response can
    // end without a final frame after the server has already committed headers.
    // Keep one content-free request-key receipt at the browser decoder so EOF,
    // release rejection and a deliberate abort cannot collapse into a false
    // claim that Board access failed. Falsifier: two receipts, private payload
    // bytes in a receipt, or any failure renders the old generic Board claim.
    try {
      options.onTerminal?.({
        schema: "ops.client-stream-terminal.v1",
        requestKeyHash,
        outcome,
        safeErrorCode,
        lastFrameKind,
        bytesReceived,
        elapsedMs: Math.max(0, Math.round(now() - startedAt)),
        httpStatus,
        contentType
      });
    } catch {
      // A diagnostic observer cannot own the visible answer or cancellation.
    }
  };
  const fail = (
    outcome: OpsLiveTurnTerminalOutcome,
    code: string,
    message: string,
    status = 502
  ): ApiError => {
    recordTerminal(outcome, code);
    return new ApiError(status, code, message);
  };
  const completeEnvelope = (envelope: OpsPublicTurnEnvelope): OpsPublicTurnEnvelope => {
    recordTerminal(
      envelope.result.response.kind === "CLARIFICATION"
        ? "SERVER_CLARIFICATION"
        : envelope.result.response.kind === "FAILURE"
          ? "SERVER_FAILURE"
          : "COMPLETED_FINAL",
      null
    );
    return envelope;
  };
  let response: Response;
  try {
    response = await fetcher("/api/ops-assistant", {
      method: "POST",
      credentials: "same-origin",
      headers: {
        "Content-Type": "application/json",
        Accept: `${OPS_LUNA_STREAM_MEDIA_TYPE}, application/json`,
        ...(options.requestKey ? { "Idempotency-Key": options.requestKey } : {})
      },
      body: JSON.stringify({
        text,
        history,
        sourceEventIds: options.sourceEventIds ?? [],
        ...(options.presentationSourceEventIds?.length ? { presentationSourceEventIds: options.presentationSourceEventIds } : {}),
        workspaceContext: options.workspaceContext ?? null
      }),
      ...(options.signal ? { signal: options.signal } : {})
    });
    httpStatus = response.status;
    const responseContentType = response.headers.get("Content-Type")?.toLowerCase() ?? "";
    contentType = responseContentType.startsWith(OPS_LUNA_STREAM_MEDIA_TYPE)
      ? "application/x-ndjson"
      : responseContentType.startsWith("application/json")
        ? "application/json"
        : "other";
  } catch (error) {
    // WHY [BOL-LUNA-ABORT-AUTHORITY-001]: an Error name is transport-controlled
    // and cannot prove which request was cancelled. Only the exact signal bound
    // to this fetch owns ABORTED; every other thrown shape is a transport failure.
    if (requestWasAborted()) {
      recordTerminal("ABORTED", "REQUEST_ABORTED");
      throw error;
    }
    throw fail(
      "TRANSPORT_READ_FAILED",
      "STREAM_TRANSPORT_FAILED",
      "The connection ended before Luna finished. Try again."
    );
  }
  // WHY [BOL-LUNA-TRUTHFUL-PROGRESS-001]: a network connection is not evidence
  // that Luna is checking tools/current facts. Basic conversation must read as
  // an immediate response. Only the same-origin brain can explicitly receipt a
  // real check; the client never guesses from wording or invents progress.
  let requiresCheck = response.headers.get("X-Ops-Luna-Progress")?.toLowerCase() === "checking";
  options.onProgress?.({
    phase: requiresCheck ? "checking" : "connected",
    requiresCheck,
    elapsedMs: now() - startedAt,
    bytesReceived: 0
  });

  const decoder = new TextDecoder();
  let raw = "";
  let lineBuffer = "";
  let streamPayload: unknown = null;
  let streamFinalSeen = false;
  let validatedStreamEnvelope: OpsPublicTurnEnvelope | null = null;
  let lastHeartbeatElapsedMs = 0;
  const isEventStream = contentType === "application/x-ndjson";
  const consumeEventLine = (line: string): void => {
    const normalized = line.trim();
    if (!normalized) return;
    let event: unknown;
    try {
      event = JSON.parse(normalized);
    } catch {
      throw fail("INVALID_FRAME", "STREAM_INVALID_FRAME", "Luna sent a response this app couldn't read safely. Try again.");
    }
    if (!event || typeof event !== "object" || !("type" in event)) {
      throw fail("INVALID_FRAME", "STREAM_INVALID_FRAME", "Luna sent a response this app couldn't read safely. Try again.");
    }
    const frame = event as {
      type?: unknown;
      phase?: unknown;
      response?: unknown;
      error?: unknown;
      text?: unknown;
    };
    // A terminal frame closes the response contract. Accepting later progress
    // would let a stale/broken gateway make Voice speak a hold cue after Luna's
    // answer is already authoritative.
    if (streamFinalSeen) {
      throw fail("INVALID_FRAME", "STREAM_INVALID_FRAME", "Luna sent a response this app couldn't read safely. Try again.");
    }
    if (frame.type === "progress" && frame.phase === "checking") {
      lastFrameKind = "progress";
      if (!requiresCheck) {
        requiresCheck = true;
        options.onProgress?.({
          phase: "checking",
          requiresCheck: true,
          elapsedMs: now() - startedAt,
          bytesReceived
        });
      }
      return;
    }
    if (frame.type === "model-progress"
      && typeof frame.text === "string"
      && frame.text.trim().length > 0
      && !/[\r\n]/u.test(frame.text)
      && isUnicodeScalarTextWithinLength(frame.text, 240)
      && Object.keys(frame).length === 2) {
      lastFrameKind = "progress";
      try { options.onModelProgress?.(frame.text); } catch {
        // Presentation callbacks never own stream decoding or the final result.
      }
      return;
    }
    if (frame.type === "heartbeat") {
      lastFrameKind = "heartbeat";
      options.onProgress?.({
        phase: "heartbeat",
        requiresCheck,
        elapsedMs: now() - startedAt,
        bytesReceived
      });
      return;
    }
    if (frame.type === "final" && frame.response && typeof frame.response === "object") {
      lastFrameKind = "final";
      streamFinalSeen = true;
      streamPayload = frame.response;
      if (response.ok) {
        validatedStreamEnvelope = decodeOpsPublicTurnEnvelope(frame.response, options.expectedReleaseId);
        if (!validatedStreamEnvelope) {
          throw fail(
            "RELEASE_ENVELOPE_REJECTED",
            "RELEASE_ENVELOPE_REJECTED",
            "Luna's response couldn't be verified for this version of the app. Refresh, then try again."
          );
        }
      }
      return;
    }
    if (frame.type === "error" && frame.error && typeof frame.error === "object") {
      lastFrameKind = "error";
      streamFinalSeen = true;
      streamPayload = { error: frame.error };
      return;
    }
    throw fail("INVALID_FRAME", "STREAM_INVALID_FRAME", "Luna sent a response this app couldn't read safely. Try again.");
  };
  const consumeEventChunk = (chunk: string, flush = false): void => {
    lineBuffer += chunk;
    let boundary = lineBuffer.indexOf("\n");
    while (boundary >= 0) {
      const line = lineBuffer.slice(0, boundary);
      lineBuffer = lineBuffer.slice(boundary + 1);
      consumeEventLine(line);
      boundary = lineBuffer.indexOf("\n");
    }
    if (flush && lineBuffer.trim()) consumeEventLine(lineBuffer);
    if (flush) lineBuffer = "";
  };
  try {
    if (response.body) {
      const reader = response.body.getReader();
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        bytesReceived += value.byteLength;
        if (bytesReceived > MAX_RESPONSE_BYTES) {
          await reader.cancel("response-too-large").catch(() => undefined);
          throw fail("RESPONSE_TOO_LARGE", "RESPONSE_TOO_LARGE", "Luna returned more data than this chat can safely display.");
        }
        const chunk = decoder.decode(value, { stream: true });
        if (isEventStream) consumeEventChunk(chunk);
        else raw += chunk;
        const elapsedMs = now() - startedAt;
        if (!isEventStream && !chunk.trim() && elapsedMs >= 1_000 && elapsedMs - lastHeartbeatElapsedMs >= 1_000) {
          lastHeartbeatElapsedMs = elapsedMs;
          options.onProgress?.({
            phase: "heartbeat",
            requiresCheck,
            elapsedMs,
            bytesReceived
          });
        }
      }
      const tail = decoder.decode();
      if (isEventStream) consumeEventChunk(tail, true);
      else raw += tail;
    } else {
      raw = await response.text();
      bytesReceived = new TextEncoder().encode(raw).byteLength;
      if (isEventStream) consumeEventChunk(raw, true);
    }
  } catch (error) {
    if (error instanceof ApiError) throw error;
    // A newline-complete final that already passed the independent release
    // decoder is authoritative. Later reader teardown cannot revoke it, while
    // any parsed post-final frame still fails above before reaching this path.
    if (validatedStreamEnvelope) return completeEnvelope(validatedStreamEnvelope);
    if (requestWasAborted()) {
      recordTerminal("ABORTED", "REQUEST_ABORTED");
      throw error;
    }
    throw fail(
      "TRANSPORT_READ_FAILED",
      "STREAM_TRANSPORT_FAILED",
      "The connection ended before Luna finished. Try again."
    );
  }

  // WHY [BOL-LUNA-EARLY-CHECK-001]: the old final-only JSON response could not
  // tell Voice that a real tool/fact stage had started until the answer was
  // already done. Opt-in NDJSON carries only bounded stage enums and one final
  // existing response, so the exact spoken hold line can begin immediately
  // without leaking prompts, evidence, or chain-of-thought. Legacy JSON and its
  // whitespace heartbeats remain accepted. Falsifier: basic chat emits
  // checking, a stream produces two answers, or any event contains private text.
  if (validatedStreamEnvelope) return completeEnvelope(validatedStreamEnvelope);
  const payload = isEventStream
    ? streamFinalSeen ? streamPayload : (() => {
        throw fail(
          "PREMATURE_EOF",
          "STREAM_PREMATURE_EOF",
          "Luna's response ended before a final answer arrived. Try again."
        );
      })()
    : (() => {
        try {
          return parsePayload(raw);
        } catch {
          throw fail("INVALID_FRAME", "STREAM_INVALID_FRAME", "Luna sent a response this app couldn't read safely. Try again.");
        }
      })();
  // WHY [BOL-PUBLIC-TURN-COMMIT-AMBIGUITY-001]: streaming commits HTTP 200
  // before PostgreSQL's final outcome is known. This one closed error code is
  // therefore a transport terminal, not a release-bound TurnResult. Render its
  // local fixed message without installing it in history or mislabeling it as
  // a release-envelope rejection. Falsifier: these bytes become a public turn,
  // an arbitrary 200 error message is trusted, or JSON and NDJSON disagree.
  const noncanonicalTransportError = readNoncanonicalPersistenceError(payload);
  if (response.ok && noncanonicalTransportError) {
    lastFrameKind = "error";
    throw fail(
      "NONCANONICAL_TRANSPORT_ERROR",
      noncanonicalTransportError.code,
      noncanonicalTransportError.message
    );
  }
  if (!response.ok) {
    const error = readApiError(payload);
    if (response.status === 401 && typeof window !== "undefined") {
      window.dispatchEvent(new Event(AUTH_REQUIRED_EVENT));
    }
    const code = safeHttpErrorCode(error?.code, response.status);
    recordTerminal("HTTP_ERROR", code);
    throw new ApiError(
      response.status,
      code,
      error?.message ?? "That request did not work."
    );
  }
  // WHY [BOL-PUBLIC-TURN-WEB-DECODE-001]: a syntactically valid answer is not
  // the published turn. Text may render only the server-authored result whose
  // hashes and exact immutable release match this authenticated shell. A raw
  // response or mixed-release envelope is a transport failure, never a
  // fallback publisher. Falsifier: either payload reaches chat/history/Voice.
  const envelope = decodeOpsPublicTurnEnvelope(payload, options.expectedReleaseId);
  if (!envelope) {
    throw fail(
      "RELEASE_ENVELOPE_REJECTED",
      "RELEASE_ENVELOPE_REJECTED",
      "Luna's response couldn't be verified for this version of the app. Refresh, then try again."
    );
  }
  return completeEnvelope(envelope);
}

export function opsLiveTurnFailureMessage(error: unknown): string {
  if (error instanceof ApiError) return error.message;
  return "The connection ended before Luna finished. Try again.";
}

function parsePayload(raw: string): unknown {
  try {
    return JSON.parse(raw.trim());
  } catch {
    throw new ApiError(502, "INVALID_RESPONSE", "Luna's response could not be read safely.");
  }
}

function readApiError(payload: unknown): { code?: string; message?: string } | null {
  if (!payload || typeof payload !== "object" || !("error" in payload)) return null;
  const error = (payload as { error?: unknown }).error;
  return error && typeof error === "object" ? error as { code?: string; message?: string } : null;
}

function readNoncanonicalPersistenceError(payload: unknown): Readonly<{
  code: typeof OPS_PUBLIC_TURN_PERSISTENCE_UNRESOLVED;
  message: typeof OPS_PUBLIC_TURN_PERSISTENCE_UNRESOLVED_MESSAGE;
}> | null {
  const error = readApiError(payload);
  return error?.code === OPS_PUBLIC_TURN_PERSISTENCE_UNRESOLVED
    ? {
        code: OPS_PUBLIC_TURN_PERSISTENCE_UNRESOLVED,
        message: OPS_PUBLIC_TURN_PERSISTENCE_UNRESOLVED_MESSAGE
      }
    : null;
}

function safeHttpErrorCode(candidate: unknown, status: number): string {
  // Same-origin error JSON is still untrusted receipt input. Keep only closed
  // public API literals; otherwise derive a content-free code from HTTP status.
  if (typeof candidate === "string") {
    const safe = SAFE_HTTP_ERROR_CODES.get(candidate);
    if (safe) return safe;
  }
  return Number.isInteger(status) && status >= 400 && status <= 599
    ? `HTTP_${status}`
    : "REQUEST_FAILED";
}
